import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// LINE icon component
const LineIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
  </svg>
);

export default function Contact() {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const contactData = {
        ...formData,
        language: language,
        submittedAt: new Date().toISOString(),
      };

      await axios.post(`${API}/contacts`, contactData);
      
      setIsSubmitted(true);
      toast.success(t('contact.success'));
    } catch (error) {
      console.error('Contact submission error:', error);
      toast.error(t('contactPage.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="contact-success">
        <div className="max-w-md mx-auto text-center p-8 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="w-16 h-16 bg-[#e6f6ec] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-[#00A651]" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {t('contactPage.messageReceived')}
          </h1>
          <p className="text-gray-600 mb-6">
            {t('contact.success')}
          </p>
          <Button 
            onClick={() => window.location.href = '/'}
            className="bg-[#00A651] hover:bg-[#008a43]"
          >
            {t('contactPage.returnHome')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="contact-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('contact.title')}
          </h1>
          <p className="text-gray-600 max-w-2xl">
            {t('contact.subtitle')}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8">
              <div className="space-y-6">
                {/* Name & Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-gray-700 font-medium">
                      {t('contact.form.name')} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-medium">
                      {t('contact.form.email')} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      data-testid="contact-input-email"
                    />
                  </div>
                </div>

                {/* Phone & Subject */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone" className="text-gray-700 font-medium">
                      {t('contact.form.phone')}
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="mt-1"
                      data-testid="contact-input-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject" className="text-gray-700 font-medium">
                      {t('contact.form.subject')} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      data-testid="contact-input-subject"
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message" className="text-gray-700 font-medium">
                    {t('contact.form.message')} <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="mt-1"
                    data-testid="contact-input-message"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#00A651] hover:bg-[#008a43] text-white font-bold py-6"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      {t('contact.form.submitting')}
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      {t('contact.form.submit')}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>

          {/* Contact Info Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-24">
              <h2 className="font-bold text-lg text-gray-900 mb-6">
                {t('footer.contactInfo')}
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-[#e6f6ec] p-3 rounded-lg">
                    <MapPin className="h-5 w-5 text-[#00A651]" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 mb-1">{t('contact.info.address')}</p>
                    <p className="text-gray-600 text-sm whitespace-pre-line">
                      {language === 'ja' ? (settings?.address || t('footer.addressNotSet')) : t('footer.addressRomaji')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#e6f6ec] p-3 rounded-lg">
                    <Phone className="h-5 w-5 text-[#00A651]" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 mb-1">{t('contact.info.phone')}</p>
                    <a href={`tel:${settings?.phone || ''}`} className="text-[#00A651] hover:underline">
                      {settings?.phone || t('footer.phoneNotSet')}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#e6f6ec] p-3 rounded-lg">
                    <Mail className="h-5 w-5 text-[#00A651]" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 mb-1">{t('contact.info.email')}</p>
                    <a href={`mailto:${settings?.email || ''}`} className="text-[#00A651] hover:underline">
                      {settings?.email || t('footer.emailNotSet')}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#e6f6ec] p-3 rounded-lg">
                    <Clock className="h-5 w-5 text-[#00A651]" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 mb-1">{t('contact.info.hours')}</p>
                    <p className="text-gray-600 text-sm whitespace-pre-line">
                      {language === 'ja' ? (settings?.businessHours || t('footer.hoursNotSet')) : t('footer.businessHoursDefault')}
                    </p>
                  </div>
                </div>

                {settings?.whatsapp && (
                  <div className="flex items-start gap-4">
                    <div className="bg-[#dcfce7] p-3 rounded-lg">
                      <MessageCircle className="h-5 w-5 text-[#25D366]" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 mb-1">WhatsApp</p>
                      <a 
                        href={`https://wa.me/${settings.whatsapp.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#25D366] hover:underline"
                      >
                        {settings.whatsapp}
                      </a>
                    </div>
                  </div>
                )}

                {settings?.lineId && (
                  <div className="flex items-start gap-4">
                    <div className="bg-[#dcfce7] p-3 rounded-lg">
                      <LineIcon className="h-5 w-5 text-[#00B900]" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 mb-1">LINE</p>
                      <a 
                        href={`https://line.me/ti/p/${settings.lineId.startsWith('@') ? settings.lineId : '@' + settings.lineId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#00B900] hover:underline"
                      >
                        {settings.lineId}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
